import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Box,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import api from "api/axios"; // ✅ Remplace axios par l'instance configurée
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const BudgetEditor = () => {
  const query = useQuery();
  const forcedBudgetName = query.get("name");

  const [budgetMap, setBudgetMap] = useState({});
  const [accountTypes, setAccountTypes] = useState({});
  const [originalBudgetMap, setOriginalBudgetMap] = useState({});
  const [newAccounts, setNewAccounts] = useState([]);
  const [budgetName, setBudgetName] = useState("");
  const [budgetType, setBudgetType] = useState("ProfitAndLoss");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const fetchBudgetAndTypes = async () => {
      setLoading(true);
      try {
        const budgetRes = await api.get("/quickbooks/budgets/");
        const typesRes = await api.get("/quickbooks/accounts/types/");

        const forced = forcedBudgetName || localStorage.getItem("forceDisplayBudget");

        const getDateFromName = (name) => {
          const match = name.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/);
          return match ? new Date(match[0]) : new Date(0);
        };

        const sortedBudgets = [...budgetRes.data.budgets].sort(
          (a, b) => getDateFromName(b.Name) - getDateFromName(a.Name)
        );

        const latestBudget = sortedBudgets.find((b) => b.Name === forced) || sortedBudgets[0];
        const organized = {};

        latestBudget.BudgetDetail.forEach(({ BudgetDate, Amount, AccountRef }) => {
          const month = new Date(BudgetDate).toLocaleString("en-US", { month: "short" });
          if (!organized[AccountRef.name]) organized[AccountRef.name] = {};
          organized[AccountRef.name][month] = Amount;
        });

        setBudgetMap(organized);
        setOriginalBudgetMap(JSON.parse(JSON.stringify(organized)));
        setBudgetName(latestBudget.Name);
        setBudgetType(latestBudget.BudgetType || "ProfitAndLoss");

        setAccountTypes(typesRes.data.account_types || {});
        localStorage.removeItem("forceDisplayBudget");
      } catch (err) {
        console.error("Erreur chargement budget/types :", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBudgetAndTypes();
  }, [forcedBudgetName]);

  const isModified = useCallback(
    () => JSON.stringify(budgetMap) !== JSON.stringify(originalBudgetMap) || newAccounts.length > 0,
    [budgetMap, originalBudgetMap, newAccounts]
  );

  const handleAmountChange = (account, month, value) => {
    const numericValue = Math.max(0, parseFloat(value) || 0);
    setBudgetMap((prev) => ({
      ...prev,
      [account]: {
        ...prev[account],
        [month]: numericValue,
      },
    }));
  };

  const addNewAccount = () => {
    setNewAccounts((prev) => [
      ...prev,
      { id: Date.now(), name: "", values: months.reduce((acc, m) => ({ ...acc, [m]: 0 }), {}) },
    ]);
  };

  const updateNewAccount = (id, field, value) => {
    setNewAccounts((prev) =>
      prev.map((acc) =>
        acc.id === id
          ? field === "name"
            ? { ...acc, name: value }
            : { ...acc, values: { ...acc.values, [field]: value } }
          : acc
      )
    );
  };

  const fillEntireAccount = (account, value) => {
    setBudgetMap((prev) => {
      const updated = { ...prev };
      months.forEach((month) => {
        updated[account][month] = parseFloat(value) || 0;
      });
      return updated;
    });
  };

  const handleSave = async () => {
    const newMap = { ...budgetMap };

    for (const account of newAccounts) {
      const name = account.name.trim();
      if (!name) continue;

      try {
        await api.post("/quickbooks/accounts/create/", { name });
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Erreur création compte '${name}'`,
          severity: "error",
        });
        return;
      }

      newMap[name] = account.values;
    }

    const newDetails = [];
    Object.entries(newMap).forEach(([account, monthValues]) => {
      Object.entries(monthValues).forEach(([monthName, amount]) => {
        const monthIndex = months.indexOf(monthName);
        const date = new Date(new Date().getFullYear(), monthIndex, 1).toISOString().split("T")[0];
        newDetails.push({
          BudgetDate: date,
          Amount: amount,
          AccountRef: {
            name: account,
            ...(accountTypes[account] ? { AccountType: accountTypes[account] } : {}),
          },
        });
      });
    });

    const generatedName = budgetName?.trim()
      ? budgetName
      : `Budget FY${new Date().getFullYear()} - ${new Date()
          .toISOString()
          .slice(0, 16)
          .replace("T", " ")}`;

    const payload = { budget: newDetails, name: generatedName, budget_type: budgetType };

    setBudgetName(generatedName);
    localStorage.setItem("forceDisplayBudget", generatedName);
    setSaving(true);

    try {
      await api.post("/quickbooks/budgets/update/", payload);
      setSnackbar({
        open: true,
        message: `Budget "${generatedName}" sauvegardé avec succès`,
        severity: "success",
      });
      setOriginalBudgetMap(JSON.parse(JSON.stringify(newMap)));
      setBudgetMap(newMap);
      setNewAccounts([]);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erreur lors de la sauvegarde",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { Header: "Account", accessor: "account", align: "left" },
    ...months.map((month) => ({ Header: month, accessor: month, align: "right" })),
    { Header: "Quick Fill", accessor: "fill", align: "center" },
  ];

  const totalsByMonth = {};
  months.forEach((month) => (totalsByMonth[month] = 0));
  const rows = [];

  newAccounts.forEach(({ id, name, values }) => {
    const row = {
      account: (
        <TextField
          placeholder="New account"
          value={name}
          onChange={(e) => updateNewAccount(id, "name", e.target.value)}
          variant="standard"
          size="small"
        />
      ),
    };
    months.forEach((month) => {
      row[month] = (
        <TextField
          value={values[month]}
          onChange={(e) => updateNewAccount(id, month, parseFloat(e.target.value) || 0)}
          variant="standard"
          size="small"
          inputProps={{ style: { textAlign: "right" } }}
        />
      );
    });
    row.fill = "-";
    rows.push(row);
  });

  const groupedAccounts = {};
  Object.entries(budgetMap).forEach(([account, monthValues]) => {
    const type = accountTypes[account] || "Other";
    if (!groupedAccounts[type]) groupedAccounts[type] = [];
    groupedAccounts[type].push([account, monthValues]);
  });
  Object.keys(groupedAccounts)
    .sort()
    .forEach((type) => {
      rows.push({
        account: (
          <Box
            sx={{
              background: "linear-gradient(90deg, #e0e0e0, #f9f9f9)",
              fontWeight: "600",
              fontSize: "1rem",
              fontFamily: `"Public Sans", "Roboto", "Helvetica", "Arial", sans-serif`,
              color: "#333",
              padding: "8px 16px",
              borderRadius: "8px",
              marginTop: "16px",
              boxShadow: "inset 0 -1px 0 #ccc",
            }}
          >
            {type.toUpperCase()}
          </Box>
        ),
        ...months.reduce((acc, m) => ({ ...acc, [m]: "" }), {}),
        fill: "",
      });

      groupedAccounts[type].forEach(([account, monthValues]) => {
        const row = { account };
        months.forEach((month) => {
          const amount = monthValues[month] ?? 0;
          totalsByMonth[month] += amount;
          row[month] = (
            <TextField
              value={amount}
              onChange={(e) => handleAmountChange(account, month, e.target.value)}
              variant="standard"
              size="small"
              inputProps={{ style: { textAlign: "right" } }}
            />
          );
        });
        row.fill = (
          <IconButton
            size="small"
            onClick={() => {
              const amount = prompt(`Fill all months for '${account}' with:`);
              if (amount !== null) fillEntireAccount(account, amount);
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        );
        rows.push(row);
      });
    });

  rows.push({
    account: <strong>Total</strong>,
    ...Object.fromEntries(
      months.map((month) => [
        month,
        <strong key={`total-${month}`}>{totalsByMonth[month].toFixed(2)}</strong>,
      ])
    ),
    fill: "-",
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box sx={{ marginBottom: "24px" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={5}>
              <TextField
                label="Budget Name (optional)"
                value={budgetName}
                onChange={(e) => setBudgetName(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="budget-type-label">Budget Type</InputLabel>
                <Select
                  labelId="budget-type-label"
                  id="budget-type-select"
                  value={budgetType}
                  onChange={(e) => setBudgetType(e.target.value)}
                  label="Budget Type"
                >
                  <MenuItem value="ProfitAndLoss">Profit and Loss</MenuItem>
                  <MenuItem value="BalanceSheet">Balance Sheet</MenuItem>
                  <MenuItem value="CashFlow">Cash Flow</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="outlined" color="info" onClick={addNewAccount} fullWidth>
                + Ajouter un compte
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                color="success"
                onClick={handleSave}
                disabled={saving || !isModified()}
                fullWidth
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <DataTable
          table={{ columns, rows }}
          isLoading={loading}
          noEndBorder
          entriesPerPage={false}
          pagination={{ enabled: false }}
          showTotalEntries={false}
          stickyHeader
          stickyColumn
          renderAllRows
        />
      </MDBox>
      <Footer />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BudgetEditor;
